import { EmbedConfig, getEmbedQuery } from 'utils/embed.utils';
import { replacePathParams } from 'utils/routes.utils';

import { EMBED_PATH_PREFIX } from 'constants/embed.constants';
import {
  LINEUP_KINDS,
  LINEUP_VIEWS,
  LineupKind,
  LineupType,
  LineupView,
  STANDARD_LINEUP_VIEWS,
  XP_DAY_VIEWS,
  XP_NITE_VIEWS,
} from 'constants/lineup.constants';
import { Routes } from 'constants/routes.constants';

// How the scene writes a back-to-back, so it is not translated.
const B2B_SEPARATOR = ' B2B ';

// A b2b reads as everyone who plays it, the performer first.
export const getEntryBilling = (entry: {
  performer: { name: string };
  collaborators: Array<{ name: string }>;
}) => {
  if (!entry.collaborators.length) return entry.performer.name;

  return [entry.performer, ...entry.collaborators]
    .map((artist) => artist.name)
    .join(B2B_SEPARATOR);
};

export const getXpViews = (kind: LineupKind): Array<LineupView> =>
  kind === 'nite' ? XP_NITE_VIEWS : XP_DAY_VIEWS;

export const isLineupKind = (value: unknown): value is LineupKind =>
  LINEUP_KINDS.includes(value as LineupKind);

/** XP wins where an event somehow has both: it is the richer lineup. */
export const getLineupType = ({
  hasXp,
  hasStandard,
}: {
  hasXp: boolean;
  hasStandard: boolean;
}): LineupType | undefined => {
  if (hasXp) return 'xp';
  if (hasStandard) return 'standard';

  return undefined;
};

export interface LineupSettings {
  /** View slugs the lineup leaves off. */
  hiddenSections: Array<string>;
  hideStages: boolean;
  hideDates: boolean;
  hideTimes: boolean;
  headlinerOnly: boolean;
}

/** Shape shared by both lineup records, so either can be read the same way. */
interface LineupDisplayRecord {
  hideSections?: unknown;
  hideStages?: boolean;
  hideDates?: boolean;
  hideTimes?: boolean;
  headlinerOnly?: boolean;
}

/** A JSON multi-select arrives untyped, and unset it arrives null. */
const sectionsOf = (value: unknown): Array<string> =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];

/** What the lineup showing right now has chosen to hide. */
export const getLineupSettings = (
  record: LineupDisplayRecord | undefined,
): LineupSettings => ({
  hiddenSections: sectionsOf(record?.hideSections),
  hideStages: !!record?.hideStages,
  hideDates: !!record?.hideDates,
  hideTimes: !!record?.hideTimes,
  headlinerOnly: !!record?.headlinerOnly,
});

/** Views a lineup offers. Hiding all of them counts as hiding none. */
export const getLineupViews = (
  type: LineupType,
  kind?: LineupKind,
  hidden?: Array<string>,
): Array<LineupView> => {
  const configured =
    type === 'xp' ? getXpViews(kind ?? 'day') : STANDARD_LINEUP_VIEWS;

  const available = configured.filter((view) => !hidden?.includes(view));

  return available.length ? available : configured;
};

/** The view a lineup opens on. */
export const getDefaultLineupView = (
  type: LineupType,
  kind?: LineupKind,
  hidden?: Array<string>,
): LineupView => getLineupViews(type, kind, hidden)[0];

/** The kinds an event runs, in the order the tabs should offer them. */
export const getLineupKinds = (
  kinds: Array<string> | undefined,
): Array<LineupKind> =>
  LINEUP_KINDS.filter((kind) => (kinds ?? []).includes(kind));

/** The programme an XP event opens on, preferring day over nite. */
export const getDefaultLineupKind = (
  kinds: Array<string> | undefined,
): LineupKind | undefined => getLineupKinds(kinds)[0];

/**
 * Standard events have one lineup, so their views sit straight under it.
 * Embedded links keep the `/embed` prefix: only that path is framable.
 */
export const getLineupViewPath = (
  slug: string,
  view: LineupView,
  kind?: LineupKind,
  embed?: EmbedConfig,
) => {
  const path = kind
    ? replacePathParams(Routes.EventLineupKindView, { slug, kind, view })
    : replacePathParams(Routes.EventLineupView, { slug, view });

  if (!embed?.isEmbedded) return path;

  const query = getEmbedQuery(embed);

  return `${EMBED_PATH_PREFIX}${path}${query ? `?${query}` : ''}`;
};

export const getLineupEntryId = (
  value: string | Array<string> | undefined,
): string | undefined => (Array.isArray(value) ? value[0] : value) || undefined;

/** Reads the view and programme out of a lineup pathname. */
export const parseLineupPath = (pathname: string) => {
  const segments = pathname.split('/lineup/')[1]?.split('/') ?? [];

  return {
    kind: segments.find(isLineupKind),
    view: segments.find((segment): segment is LineupView =>
      LINEUP_VIEWS.includes(segment as LineupView),
    ),
  };
};
